import * as React from "react"
import { cn } from "@/lib/utils"

export interface OptimizedImageProps
    extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string
    alt: string
    width?: number | string
    height?: number | string
    fallbackSrc?: string | null
    priority?: boolean
    fetchPriority?: "high" | "low" | "auto"
    cinematic?: boolean
    placeholder?: string
    aspectRatio?: string
}

const OptimizedImage = React.forwardRef<HTMLImageElement, OptimizedImageProps>(
    ({ className, src, alt, width, height, fallbackSrc, priority = false, fetchPriority = "auto", cinematic = false, placeholder, aspectRatio, ...props }, ref) => {
        const [imgSrc, setImgSrc] = React.useState<string>(src)
        const [isLoaded, setIsLoaded] = React.useState(false)
        const [error, setError] = React.useState(false)
        const imgRef = React.useRef<HTMLImageElement>(null)

        // Combine internal ref with forwarded ref
        React.useImperativeHandle(ref, () => imgRef.current!)

        // Generate WebP URL if the source is a JPG or PNG
        const getWebPUrl = (url: string): string | null => {
            if (!url) return null
            if (/\.(jpe?g|png)(\?.*)?$/i.test(url)) {
                const basePath = url.replace(/\.(jpe?g|png)(\?.*)?$/i, "")
                const queryMatch = url.match(/(\?.*)$/i)
                const query = queryMatch ? queryMatch[1] : ""
                return `${basePath}.webp${query}`
            }
            return url
        }

        React.useEffect(() => {
            // Handle preloading for priority images (LCP)
            if (priority && src) {
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';

                const srcSet = (props as any).srcSet;
                const sizes = (props as any).sizes;

                link.href = getWebPUrl(src) || src;

                if (srcSet) link.setAttribute('imagesrcset', srcSet);
                if (sizes) link.setAttribute('imagesizes', sizes);
                if (fetchPriority === 'high') link.setAttribute('fetchpriority', 'high');

                document.head.appendChild(link);
                return () => {
                    document.head.removeChild(link);
                };
            }
        }, [src, priority, fetchPriority, props.srcSet, (props as any).sizes]);

        React.useEffect(() => {
            // Check if already loaded (e.g. from cache)
            if (imgRef.current?.complete) {
                setIsLoaded(true)
            }

            const webpSrc = getWebPUrl(src)
            if (webpSrc && webpSrc !== src) {
                const webpCheck = new Image()
                webpCheck.onload = () => setImgSrc(webpSrc)
                webpCheck.onerror = () => setImgSrc(src)
                webpCheck.src = webpSrc
            } else {
                setImgSrc(src)
            }
        }, [src])

        const handleError = () => {
            if (!error && fallbackSrc) {
                setError(true)
                setImgSrc(fallbackSrc)
            }
        }

        return (
            <div
                className={cn(
                    "relative overflow-hidden bg-[#f0f4f8]",
                    className
                )}
                style={{
                    aspectRatio: aspectRatio || (width && height ? `${width}/${height}` : undefined)
                }}
            >
                {/* Blur-up Placeholder - Skip for priority images to avoid LCP delay */}
                {placeholder && !isLoaded && !priority && (
                    <div
                        className="absolute inset-0 z-0 bg-cover bg-center transition-opacity duration-700"
                        style={{
                            backgroundImage: `url(${placeholder})`,
                            filter: 'blur(10px)',
                            transform: 'scale(1.1)'
                        }}
                    />
                )}

                <img
                    ref={imgRef}
                    src={imgSrc}
                    alt={alt}
                    width={width}
                    height={height}
                    onLoad={() => setIsLoaded(true)}
                    onError={handleError}
                    className={cn(
                        "absolute inset-0 w-full h-full object-cover z-10",
                        // Disable transition for priority images to hit LCP faster
                        !priority && "transition-opacity duration-700",
                        isLoaded ? "opacity-100" : "opacity-0",
                        cinematic && "!h-full !object-cover transition-all duration-[1200ms] ease-[cubic-bezier(0.2,0,0.2,1)] group-hover:scale-[1.2] group-hover:brightness-[1.04] will-change-[transform,filter]",
                    )}
                    fetchPriority={fetchPriority}
                    {...props}
                />
            </div>
        )
    }
)
OptimizedImage.displayName = "OptimizedImage"

export { OptimizedImage }
